import type {
  Candidate,
  DraftFormState,
  SpotlightAppEntry,
  SpotlightFeaturedReview,
  StoreReview,
} from './types';

export const FEATURED_REVIEW_MAX_LENGTH = 1000;

export function toDateTimeLocal(value: string | null): string {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const offsetMs = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

export function fromDateTimeLocal(value: string): string | null {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

export function defaultDraftForm(): DraftFormState {
  const now = new Date();
  const end = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  return {
    id: null,
    weekId: now.toISOString().slice(0, 10),
    themeTitle: '',
    headerBlurb: '',
    publishStartAt: toDateTimeLocal(now.toISOString()),
    publishEndAt: toDateTimeLocal(end.toISOString()),
    appEntries: [],
    sourceCandidateRunId: null,
  };
}

export function formatDate(value: string | null): string {
  if (!value) {
    return 'Not set';
  }

  return new Date(value).toLocaleString();
}

export function candidateToEntry(candidate: Candidate): SpotlightAppEntry {
  return {
    androidPackage: candidate.androidPackage,
    curationNote: candidate.reasons[0] ?? 'Candidate spotlight app',
    featuredReview: candidate.featuredReview ?? null,
  };
}

export function packageKey(androidPackage: string) {
  return androidPackage.trim().toLowerCase();
}

export function truncateMiddle(value: string, visible = 4): string {
  if (value.length <= visible * 2 + 1) {
    return value;
  }
  return `${value.slice(0, visible)}…${value.slice(-visible)}`;
}

export function reviewerLabel(review: StoreReview): string {
  if (review.isReviewerAccountDeleted) {
    return 'Deleted reviewer';
  }
  if (review.domain) {
    return review.domain;
  }
  if (review.walletAddress) {
    return truncateMiddle(review.walletAddress);
  }
  return 'Anonymous reviewer';
}

export function reviewAuthorName(review: StoreReview): string | null {
  if (review.isReviewerAccountDeleted) {
    return null;
  }
  if (review.domain) {
    return review.domain.slice(0, 120);
  }
  if (review.walletAddress) {
    return truncateMiddle(review.walletAddress);
  }
  return null;
}

export function reviewToFeaturedReview(
  review: StoreReview,
): SpotlightFeaturedReview | null {
  const text = (review.review ?? '').trim();
  if (!text || text.length > FEATURED_REVIEW_MAX_LENGTH) {
    return null;
  }

  const rating = Number.isFinite(review.rating)
    ? Math.min(5, Math.max(1, Math.round(review.rating)))
    : null;

  const parsedDate = new Date(review.createdAt);
  const createdAt = Number.isNaN(parsedDate.getTime())
    ? null
    : parsedDate.toISOString();

  return {
    reviewId: review.id,
    authorDisplayName: reviewAuthorName(review),
    rating,
    text,
    createdAt,
  };
}

export function isoDateDaysAgo(days: number): string {
  return new Date(Date.now() - days * 24 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10);
}

export function todayIsoDate(): string {
  return new Date().toISOString().slice(0, 10);
}
